// DevOps module — three tabs (Dashboard / Browser / List).
//
// Ported from design/DevOps.html (Claude.ai design handoff). The design has
// a 4th tab "Project 360" linked from the dashboard — that's a separate
// drill-in page and is out of scope here (would mirror the existing Project
// Overview pattern).
//
// Data sources (all live in backend/azure_devops):
//   - /azure-devops/status     → sync timestamp / connection state
//   - /azure-devops/dashboard  → KPIs, byType, topProjects, topContributors,
//                                bugsByProject, unassignedByProject
//   - /azure-devops/projects   → left-pane project list (+ name search)
//   - /azure-devops/work-items → list tab (paginated, server-filtered) and
//                                browser tab (per-project hierarchy)
//
// Design mocks several fields we don't sync from ADO yet (priority, tags,
// created_date, changed_date, sprint burndown, 12-week trend). The
// corresponding cards/columns are dropped rather than faked. See
// devops.css header comment for the full omission list.

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  GitBranch, Plus, RefreshCw, Settings as SettingsIcon,
  Download, Boxes, Bug, AlertTriangle, ListChecks,
  CircleDot, ExternalLink, Search, Filter,
} from 'lucide-react'
import { api, ApiError } from '@/lib/api/client'
import './devops.css'

// ─── Types ──────────────────────────────────────────────────────────────────

interface AdoStatus {
  connected:      boolean
  orgUrl?:        string
  projectCount?:  number
  workItemCount?: number
  lastSyncAt?:    string | null
  lastSyncStatus?: string | null
}

interface AdoProject {
  id:               string
  name:             string
  state:            string | null
  visibility:       string | null
  forgeProjectId:   number | null
  forgeProjectName: string | null
  workItemCount:    number
}

interface AdoWorkItem {
  id:            number
  projectId:     string
  projectName:   string
  title:         string
  workItemType:  string
  state:         string
  assignedTo:    string | null
  areaPath:      string | null
  iterationPath: string | null
  parentId:      number | null
  syncedAt:      string | null
}

interface AdoWorkItemsResponse {
  total:  number
  top:    number
  offset: number
  items:  AdoWorkItem[]
}

interface Dashboard {
  totals: {
    projects:       number
    workItems:      number
    openItems:      number
    openBugs:       number
    unassignedOpen: number
    contributors:   number
  }
  sync: {
    lastSyncAt:     string | null
    lastSyncStatus: string | null
    lastSyncError:  string | null
  }
  byType:              { type: string; count: number }[]
  byState:             { state: string; count: number }[]
  topProjects:         { projectId: string; name: string; openCount: number }[]
  topContributors:     { assignedTo: string; openCount: number }[]
  bugsByProject:       { projectId: string; name: string; bugCount: number }[]
  unassignedByProject: { projectId: string; name: string; unassignedCount: number }[]
  orgUrl:              string | null
}

type TabKey = 'dashboard' | 'browser' | 'list'

// ─── Helpers ────────────────────────────────────────────────────────────────

function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/[\s,]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function hueFor(s: string): number {
  let h = 0
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return h % 360
}

function avatarStyle(name: string | null | undefined): React.CSSProperties {
  if (!name) return {}
  const h = hueFor(name)
  return { background: `linear-gradient(135deg, oklch(0.6 0.14 ${h}), oklch(0.5 0.16 ${(h + 60) % 360}))` }
}

function projectIconStyle(name: string): React.CSSProperties {
  const h = hueFor(name)
  return { background: `oklch(0.55 0.16 ${h})` }
}

function projectInitials(name: string): string {
  const parts = name.split(/[\s\-_]+/).filter(Boolean)
  if (parts.length === 0) return '??'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function fmtCount(n: number): string {
  return n.toLocaleString('en-GB')
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return 'never'
  const ms = Date.now() - t
  const m  = Math.floor(ms / 60_000)
  if (m < 1)    return 'just now'
  if (m < 60)   return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 24)   return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30)   return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}

/** Type → ADO marker class. Falls back to 'task' for unknown types. */
function typeKey(t: string): string {
  const k = t.toLowerCase()
  if (k.includes('epic'))                                       return 'epic'
  if (k.includes('feature'))                                    return 'feature'
  if (k.includes('story') || k.includes('product backlog'))     return 'story'
  if (k.includes('task'))                                       return 'task'
  if (k.includes('bug'))                                        return 'bug'
  if (k.includes('issue'))                                      return 'issue'
  if (k.includes('test'))                                       return 'test'
  return 'task'
}

const TYPE_COLORS: Record<string, string> = {
  epic:    '#6F39A8',
  feature: '#A06CD5',
  story:   '#2D6CDF',
  task:    '#C9963E',
  bug:     '#B53939',
  issue:   '#C9663E',
  test:    '#4A8A3A',
}

const TYPE_DISPLAY: Record<string, string> = {
  epic:    'Epic',
  feature: 'Feature',
  story:   'User Story',
  task:    'Task',
  bug:     'Bug',
  issue:   'Issue',
  test:    'Test Case',
}

const LANE_ORDER: string[] = ['epic', 'feature', 'story', 'task', 'bug', 'issue', 'test']

/** Map an ADO state string to a CSS state-pill class. */
function stateKey(s: string): string {
  const k = (s || '').toLowerCase()
  if (k === 'new' || k === 'to do' || k === 'proposed')                return 'new'
  if (k === 'active' || k === 'in progress' || k === 'committed')      return 'active'
  if (k === 'resolved' || k === 'done' || k === 'closed' || k === 'completed') return k === 'resolved' ? 'resolved' : 'closed'
  if (k === 'removed' || k === 'cancelled')                            return 'removed'
  return 'new'
}

const OPEN_STATES = new Set(['new', 'active', 'resolved'])

// ─── Module page ────────────────────────────────────────────────────────────

export default function DevOpsModule() {
  const [tab, setTab] = useState<TabKey>(() => {
    try { return (localStorage.getItem('devops-tab') as TabKey) ?? 'dashboard' }
    catch { return 'dashboard' }
  })
  useEffect(() => {
    try { localStorage.setItem('devops-tab', tab) } catch { /* noop */ }
  }, [tab])

  const status = useQuery({
    queryKey: ['azure-devops', 'status'],
    queryFn:  () => api.get<AdoStatus>('/azure-devops/status'),
  })

  if (status.isLoading) {
    return <div className="page dv-page"><div className="dv-state-msg">Loading Azure DevOps…</div></div>
  }
  if (status.isError) {
    const e = status.error as ApiError | Error
    return (
      <div className="page dv-page">
        <div className="dv-state-msg err">Couldn't load Azure DevOps status — {e.message}</div>
      </div>
    )
  }
  if (!status.data?.connected) {
    return (
      <div className="page dv-page">
        <div className="dv-hero" style={{ borderRadius: 12, borderBottom: '1px solid var(--ns-border)' }}>
          <div className="dv-hero-left">
            <div className="dv-icon"><GitBranch size={28} /></div>
            <div className="dv-hero-title">
              <div className="dv-crumbs"><span>Resources</span><span>›</span><span>Azure DevOps</span></div>
              <h1>Azure DevOps</h1>
              <div className="dv-hero-meta"><div>Not connected. Configure the integration to start syncing projects and work items.</div></div>
            </div>
          </div>
          <div className="dv-hero-actions">
            <a className="dv-btn primary" href="/settings/integrations/azure-devops"><SettingsIcon size={14} />Configure</a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page dv-page">
      <Hero status={status.data} />
      <Tabs current={tab} onChange={setTab} totalWorkItems={status.data.workItemCount ?? 0} />

      {tab === 'dashboard' && <DashboardPanel />}
      {tab === 'browser'   && <BrowserPanel  />}
      {tab === 'list'      && <ListPanel     />}
    </div>
  )
}

// ─── Hero ───────────────────────────────────────────────────────────────────

function Hero({ status }: { status: AdoStatus }) {
  const lastSyncStatus = status.lastSyncStatus ?? 'unknown'
  const lastSync       = status.lastSyncAt ? fmtRelative(status.lastSyncAt) : 'never'
  const syncClass =
    lastSyncStatus === 'success' ? '' :
    lastSyncStatus === 'partial' ? ' warn' :
    lastSyncStatus === 'failed'  ? ' bad'  : ' warn'
  return (
    <div className="dv-hero">
      <div className="dv-hero-left">
        <div className="dv-icon" title="Azure DevOps">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M21 6.5L17 3v3.2L9.5 8.3 4.5 5v3l-2 1v7l3 1v3.5l8.5-2.5L21 19V6.5zM6.5 17.1V8.4l4.6-1.3v9.7l-4.6.3zm6.6-1.5V6.9l5.5 1.6v6.7l-5.5.4z"/>
          </svg>
        </div>
        <div className="dv-hero-title">
          <div className="dv-crumbs"><span>Resources</span><span>›</span><span>Azure DevOps</span></div>
          <h1>
            Azure DevOps
            <span className={`dv-pill-sync${syncClass}`}>Sync {lastSyncStatus} · {lastSync}</span>
          </h1>
          <div className="dv-hero-meta">
            <div><Boxes size={14} /><strong>{fmtCount(status.projectCount ?? 0)}</strong> projects</div>
            <div><ListChecks size={14} /><strong>{fmtCount(status.workItemCount ?? 0)}</strong> work items</div>
            {status.orgUrl && (
              <div title={status.orgUrl}>
                <ExternalLink size={14} />
                <a href={status.orgUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                  <strong>{status.orgUrl.replace(/^https?:\/\//, '')}</strong>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="dv-hero-actions">
        <ForceSyncButton />
        <a className="dv-btn" href="/settings/integrations/azure-devops"><SettingsIcon size={14} />Settings</a>
        <button className="dv-btn primary" disabled title="CSV export — coming soon"><Download size={14} />Export view</button>
      </div>
    </div>
  )
}

function ForceSyncButton() {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const onClick = async () => {
    setBusy(true); setErr(null)
    try {
      await api.post('/azure-devops/sync', {})
      // Soft refresh — let useQuery refetch on next focus.
      window.location.reload()
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Sync failed'
      setErr(m)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button className="dv-btn" onClick={onClick} disabled={busy} title={err ?? 'Trigger an immediate sync'}>
        <RefreshCw size={14} className={busy ? 'spin' : ''} />
        {busy ? 'Syncing…' : 'Force sync'}
      </button>
      <style>{`@keyframes dv-spin{to{transform:rotate(360deg)}}.spin{animation:dv-spin .9s linear infinite}`}</style>
    </>
  )
}

// ─── Tabs ───────────────────────────────────────────────────────────────────

function Tabs({ current, onChange, totalWorkItems }: { current: TabKey; onChange: (t: TabKey) => void; totalWorkItems: number }) {
  return (
    <div className="dv-tabs" role="tablist">
      <button role="tab" aria-selected={current === 'dashboard'} className={current === 'dashboard' ? 'on' : ''} onClick={() => onChange('dashboard')}>
        <Boxes size={14} />Dashboard
      </button>
      <button role="tab" aria-selected={current === 'browser'} className={current === 'browser' ? 'on' : ''} onClick={() => onChange('browser')}>
        <GitBranch size={14} />Browser
      </button>
      <button role="tab" aria-selected={current === 'list'} className={current === 'list' ? 'on' : ''} onClick={() => onChange('list')}>
        <ListChecks size={14} />List <span className="count">{fmtCount(totalWorkItems)}</span>
      </button>
    </div>
  )
}

// ─── Dashboard panel ────────────────────────────────────────────────────────

function DashboardPanel() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['azure-devops', 'dashboard'],
    queryFn:  () => api.get<Dashboard>('/azure-devops/dashboard'),
  })
  if (isLoading) return <div className="dv-state-msg">Loading dashboard…</div>
  if (isError)  {
    const e = error as ApiError | Error
    return <div className="dv-state-msg err">Couldn't load dashboard — {e.message}</div>
  }
  if (!data) return null

  const closedShare = data.totals.workItems > 0
    ? Math.round(((data.totals.workItems - data.totals.openItems) / data.totals.workItems) * 100)
    : 0
  const unassignedPctOfOpen = data.totals.openItems > 0
    ? Math.round((data.totals.unassignedOpen / data.totals.openItems) * 100)
    : 0
  const bugPctOfOpen = data.totals.openItems > 0
    ? Math.round((data.totals.openBugs / data.totals.openItems) * 100)
    : 0

  return (
    <section>
      {/* KPI strip */}
      <div className="dv-kpis">
        <div className="dv-kpi">
          <div className="dv-kpi-label">Open work items</div>
          <div className="dv-kpi-val">{fmtCount(data.totals.openItems)}</div>
          <div className="dv-kpi-sub">{closedShare}% closed lifetime</div>
        </div>
        <div className="dv-kpi">
          <div className="dv-kpi-label">All work items</div>
          <div className="dv-kpi-val">{fmtCount(data.totals.workItems)}</div>
          <div className="dv-kpi-sub">across {fmtCount(data.totals.projects)} projects</div>
        </div>
        <div className="dv-kpi">
          <div className="dv-kpi-label">Open bugs</div>
          <div className="dv-kpi-val danger">{fmtCount(data.totals.openBugs)}</div>
          <div className="dv-kpi-sub danger">{bugPctOfOpen}% of open items</div>
        </div>
        <div className="dv-kpi">
          <div className="dv-kpi-label">Unassigned</div>
          <div className="dv-kpi-val">{fmtCount(data.totals.unassignedOpen)}</div>
          <div className="dv-kpi-sub warn">{unassignedPctOfOpen}% of open items</div>
        </div>
        <div className="dv-kpi">
          <div className="dv-kpi-label">Contributors</div>
          <div className="dv-kpi-val">{fmtCount(data.totals.contributors)}</div>
          <div className="dv-kpi-sub">unique assignees</div>
        </div>
        <div className="dv-kpi">
          <div className="dv-kpi-label">Projects</div>
          <div className="dv-kpi-val">{fmtCount(data.totals.projects)}</div>
          <div className="dv-kpi-sub">synced from ADO</div>
        </div>
      </div>

      {/* Row 1: By-type donut spans full width (we don't have the 12-week trend chart) */}
      <section className="dv-dash-row-1">
        <ByStateCard byState={data.byState} totalOpen={data.totals.openItems} totalAll={data.totals.workItems} />
        <ByTypeDonut byType={data.byType} totalOpen={data.totals.openItems} />
      </section>

      {/* Row 2: most active projects · most active contributors · bugs by project */}
      <section className="dv-dash-row-2">
        <TopProjectsCard projects={data.topProjects} />
        <TopContributorsCard contributors={data.topContributors} />
        <BugsByProjectCard bugs={data.bugsByProject} totalBugs={data.totals.openBugs} />
      </section>

      {/* Row 3: unassigned by project (placed on its own; matches design "Unassigned items" card) */}
      <section className="dv-dash-row-3">
        <UnassignedByProjectCard projects={data.unassignedByProject} total={data.totals.unassignedOpen} />
        <div /> {/* placeholder for symmetry; stale-items card from design is omitted (no changed_date) */}
      </section>
    </section>
  )
}

function ByStateCard({ byState, totalOpen, totalAll }: { byState: { state: string; count: number }[]; totalOpen: number; totalAll: number }) {
  // Group server states into the four state buckets we visualise.
  const buckets = { new: 0, active: 0, resolved: 0, closed: 0, removed: 0 }
  for (const r of byState) {
    const k = stateKey(r.state) as keyof typeof buckets
    buckets[k] = (buckets[k] ?? 0) + r.count
  }
  const openTotal = buckets.new + buckets.active + buckets.resolved || totalOpen
  return (
    <div className="dv-card">
      <div className="dv-card-head">
        <div>
          <div className="dv-card-title">Open work by state</div>
          <div className="dv-card-sub">{fmtCount(totalOpen)} open · {fmtCount(totalAll)} lifetime</div>
        </div>
      </div>
      <div className="dv-card-body">
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { label: 'New / Proposed', value: buckets.new,      cls: 'new' },
            { label: 'Active',          value: buckets.active,   cls: 'active' },
            { label: 'Resolved',        value: buckets.resolved, cls: 'resolved' },
            { label: 'Closed',          value: buckets.closed,   cls: 'closed' },
            { label: 'Removed',         value: buckets.removed,  cls: 'removed' },
          ].map(b => (
            <div key={b.label}>
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{fmtCount(b.value)}</div>
              <div style={{ fontSize: 12, color: 'var(--ns-text-subtle)', marginTop: 2 }}>{b.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'oklch(from var(--ns-text) l c h / 0.06)' }}>
          {[
            { cls: 'new',      pct: openTotal ? (buckets.new      / openTotal) * 100 : 0, bg: 'var(--ns-text-subtle)' },
            { cls: 'active',   pct: openTotal ? (buckets.active   / openTotal) * 100 : 0, bg: '#2D6CDF' },
            { cls: 'resolved', pct: openTotal ? (buckets.resolved / openTotal) * 100 : 0, bg: 'var(--ns-ok)' },
          ].map((s, i) => <div key={i} style={{ width: `${s.pct}%`, background: s.bg }} />)}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 12, color: 'var(--ns-text-muted)', flexWrap: 'wrap' }}>
          <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--ns-text-subtle)', marginRight: 6, verticalAlign: 'middle' }} />New / Proposed</div>
          <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#2D6CDF', marginRight: 6, verticalAlign: 'middle' }} />Active</div>
          <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--ns-ok)', marginRight: 6, verticalAlign: 'middle' }} />Resolved</div>
        </div>
      </div>
    </div>
  )
}

function ByTypeDonut({ byType, totalOpen }: { byType: { type: string; count: number }[]; totalOpen: number }) {
  const grouped: Record<string, number> = {}
  for (const r of byType) {
    const k = typeKey(r.type)
    grouped[k] = (grouped[k] ?? 0) + r.count
  }
  const rows = LANE_ORDER
    .map(k => ({ key: k, count: grouped[k] ?? 0 }))
    .filter(r => r.count > 0)
  const total = rows.reduce((s, r) => s + r.count, 0) || 1

  // Build dasharray offsets around a r=35 circle (circumference 219.9).
  const C = 2 * Math.PI * 35
  let acc = 0
  const segments = rows.map(r => {
    const len = (r.count / total) * C
    const seg = { color: TYPE_COLORS[r.key], dash: len, offset: -acc }
    acc += len
    return seg
  })

  return (
    <div className="dv-card">
      <div className="dv-card-head">
        <div>
          <div className="dv-card-title">Open by type</div>
          <div className="dv-card-sub">{fmtCount(totalOpen)} items</div>
        </div>
      </div>
      <div className="dv-card-body">
        <div className="dv-by-type-row">
          <div className="dv-donut-wrap">
            <svg viewBox="0 0 100 100" width="140" height="140">
              <circle cx="50" cy="50" r="35" fill="none" stroke="oklch(0.95 0.005 220)" strokeWidth="14" />
              {segments.map((s, i) => (
                <circle key={i} cx="50" cy="50" r="35" fill="none"
                  stroke={s.color} strokeWidth="14"
                  strokeDasharray={`${s.dash} ${C - s.dash}`}
                  strokeDashoffset={s.offset}
                  transform="rotate(-90 50 50)" />
              ))}
            </svg>
            <div className="dv-donut-label">
              <div>
                <div className="n">{fmtCount(totalOpen)}</div>
                <div className="l">Open</div>
              </div>
            </div>
          </div>
          <div className="dv-legend-stack">
            {rows.map(r => (
              <div key={r.key} className="dv-leg-row">
                <span className="dv-leg-sw" style={{ background: TYPE_COLORS[r.key] }} />
                <span className="nm">{TYPE_DISPLAY[r.key] ?? r.key}</span>
                <span className="ct">{fmtCount(r.count)}</span>
                <span className="pct">{Math.round((r.count / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function TopProjectsCard({ projects }: { projects: Dashboard['topProjects'] }) {
  const max = projects.reduce((m, p) => Math.max(m, p.openCount), 1)
  return (
    <div className="dv-card">
      <div className="dv-card-head">
        <div>
          <div className="dv-card-title">Most active projects</div>
          <div className="dv-card-sub">By open work-item count</div>
        </div>
      </div>
      <table className="dv-table">
        <thead><tr><th>Project</th><th className="r">Open</th><th className="r">Share</th></tr></thead>
        <tbody>
          {projects.map(p => (
            <tr key={p.projectId}>
              <td>
                <div className="dv-proj-cell">
                  <span className="dv-proj-dot" style={projectIconStyle(p.name)} />
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                </div>
              </td>
              <td className="r">{fmtCount(p.openCount)}</td>
              <td className="r" style={{ minWidth: 110 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                  <div className="dv-pbar" style={{ width: 80 }}><div style={{ width: `${(p.openCount / max) * 100}%` }} /></div>
                </div>
              </td>
            </tr>
          ))}
          {projects.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--ns-text-subtle)' }}>No open items.</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

function TopContributorsCard({ contributors }: { contributors: Dashboard['topContributors'] }) {
  const max = contributors.reduce((m, c) => Math.max(m, c.openCount), 1)
  return (
    <div className="dv-card">
      <div className="dv-card-head">
        <div>
          <div className="dv-card-title">Most active contributors</div>
          <div className="dv-card-sub">By open assigned items</div>
        </div>
      </div>
      <div className="dv-card-body">
        <div className="dv-user-list">
          {contributors.map(c => (
            <div className="dv-user-row" key={c.assignedTo}>
              <div className="dv-av md" style={avatarStyle(c.assignedTo)}>{initials(c.assignedTo)}</div>
              <div><div className="nm">{c.assignedTo}</div></div>
              <div className="barmini"><div style={{ width: `${(c.openCount / max) * 100}%` }} /></div>
              <div className="ct">{fmtCount(c.openCount)}</div>
            </div>
          ))}
          {contributors.length === 0 && <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', fontSize: 13 }}>No assigned work items.</div>}
        </div>
      </div>
    </div>
  )
}

function BugsByProjectCard({ bugs, totalBugs }: { bugs: Dashboard['bugsByProject']; totalBugs: number }) {
  const max = bugs.reduce((m, b) => Math.max(m, b.bugCount), 1)
  return (
    <div className="dv-card">
      <div className="dv-card-head">
        <div>
          <div className="dv-card-title">Open bugs by project</div>
          <div className="dv-card-sub">{fmtCount(totalBugs)} total</div>
        </div>
      </div>
      <div className="dv-card-body">
        <div className="dv-hbar-list">
          {bugs.map(b => (
            <div className="dv-hbar-row" key={b.projectId}>
              <span className="nm">{b.name}</span>
              <span className="num">{fmtCount(b.bugCount)}</span>
              <div className="dv-hbar-track">
                <div className="dv-hbar-seg" style={{ width: `${(b.bugCount / max) * 100}%` }} />
              </div>
            </div>
          ))}
          {bugs.length === 0 && <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', fontSize: 13 }}>No open bugs.</div>}
        </div>
      </div>
    </div>
  )
}

function UnassignedByProjectCard({ projects, total }: { projects: Dashboard['unassignedByProject']; total: number }) {
  const max = projects.reduce((m, p) => Math.max(m, p.unassignedCount), 1)
  return (
    <div className="dv-card">
      <div className="dv-card-head">
        <div>
          <div className="dv-card-title">Unassigned items</div>
          <div className="dv-card-sub">{fmtCount(total)} open items across the top {projects.length} projects</div>
        </div>
      </div>
      <div className="dv-card-body">
        <div className="dv-hbar-list">
          {projects.map(p => (
            <div className="dv-hbar-row" key={p.projectId}>
              <span className="nm">{p.name}</span>
              <span className="num">{fmtCount(p.unassignedCount)}</span>
              <div className="dv-hbar-track">
                <div className="dv-hbar-seg unassigned" style={{ width: `${(p.unassignedCount / max) * 100}%` }} />
              </div>
            </div>
          ))}
          {projects.length === 0 && <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', fontSize: 13 }}>Nothing unassigned.</div>}
        </div>
      </div>
    </div>
  )
}

// ─── Browser panel ──────────────────────────────────────────────────────────

function BrowserPanel() {
  const projectsQ = useQuery({
    queryKey: ['azure-devops', 'projects'],
    queryFn:  () => api.get<AdoProject[]>('/azure-devops/projects'),
  })

  const [search, setSearch] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null)

  // Auto-select the first project once loaded.
  useEffect(() => {
    if (!selectedProjectId && projectsQ.data?.length) {
      const top = [...projectsQ.data].sort((a, b) => b.workItemCount - a.workItemCount)[0]
      setSelectedProjectId(top.id)
    }
  }, [projectsQ.data, selectedProjectId])

  const filteredProjects = useMemo(() => {
    const list = projectsQ.data ?? []
    const term = search.toLowerCase().trim()
    const sorted = [...list].sort((a, b) => b.workItemCount - a.workItemCount)
    if (!term) return sorted
    return sorted.filter(p => p.name.toLowerCase().includes(term))
  }, [projectsQ.data, search])

  const workItemsQ = useQuery({
    queryKey: ['azure-devops', 'work-items', selectedProjectId],
    queryFn:  () => api.get<AdoWorkItemsResponse>(`/azure-devops/work-items?projectId=${encodeURIComponent(selectedProjectId!)}&state=open&top=2000`),
    enabled:  !!selectedProjectId,
  })

  const allItems = workItemsQ.data?.items ?? []
  const itemsById = useMemo(() => {
    const m = new Map<number, AdoWorkItem>()
    for (const i of allItems) m.set(i.id, i)
    return m
  }, [allItems])

  // Group items by lane (type bucket).
  const byLane = useMemo(() => {
    const m: Record<string, AdoWorkItem[]> = {}
    for (const i of allItems) {
      const k = typeKey(i.workItemType)
      ;(m[k] ?? (m[k] = [])).push(i)
    }
    return m
  }, [allItems])

  const selectedProject = projectsQ.data?.find(p => p.id === selectedProjectId) ?? null
  const selectedItem    = selectedItemId != null ? itemsById.get(selectedItemId) ?? null : null

  return (
    <div className="dv-browser">
      {/* LEFT — projects */}
      <div className="dv-pane dv-projects-pane">
        <div className="dv-pane-head">
          <div className="t">Projects · {fmtCount(projectsQ.data?.length ?? 0)}</div>
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-subtle)' }} />
          <input className="dv-search" placeholder="Search projects…" value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 30 }} />
        </div>
        <div className="dv-pane-body">
          <div className="dv-pane-section">All projects</div>
          <div className="dv-pj-list">
            {filteredProjects.map(p => (
              <button
                key={p.id}
                className={`dv-pj-item${p.id === selectedProjectId ? ' on' : ''}`}
                onClick={() => { setSelectedProjectId(p.id); setSelectedItemId(null) }}
              >
                <div className="dv-pj-icon" style={projectIconStyle(p.name)}>{projectInitials(p.name)}</div>
                <div className="dv-pj-name" title={p.name}>{p.name}</div>
                <div className="dv-pj-ct">{fmtCount(p.workItemCount)}</div>
              </button>
            ))}
            {filteredProjects.length === 0 && projectsQ.data && (
              <div style={{ padding: '12px 10px', color: 'var(--ns-text-subtle)', fontSize: 12 }}>No projects match.</div>
            )}
          </div>
        </div>
      </div>

      {/* CENTER — hierarchy canvas */}
      <div className="dv-pane">
        <div className="dv-crumb-bar">
          <span className="seg on">
            <Boxes size={11} />
            {selectedProject ? selectedProject.name : 'Pick a project'}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ns-text-subtle)' }}>
            {LANE_ORDER.map(k => byLane[k]?.length ?? 0)
              .map((n, i) => ({ k: LANE_ORDER[i], n }))
              .filter(x => x.n > 0)
              .map(x => `${x.n} ${TYPE_DISPLAY[x.k].toLowerCase()}${x.n === 1 ? '' : 's'}`)
              .join(' · ') || (selectedProject ? '0 open items' : '')}
          </span>
        </div>
        <div className="dv-canvas">
          {!selectedProject && (
            <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', padding: '40px 16px', fontSize: 13 }}>
              Pick a project on the left to browse its work-item hierarchy.
            </div>
          )}
          {selectedProject && workItemsQ.isLoading && (
            <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', padding: '40px 16px', fontSize: 13 }}>
              Loading work items…
            </div>
          )}
          {selectedProject && workItemsQ.isError && (
            <div style={{ textAlign: 'center', color: 'var(--ns-err)', padding: '40px 16px', fontSize: 13 }}>
              Couldn't load work items.
            </div>
          )}
          {selectedProject && !workItemsQ.isLoading && !workItemsQ.isError && (
            <>
              {/* Project lane */}
              <div className="dv-lane">
                <div className="dv-lane-label">
                  <div className="ll-ct">1</div>
                  <div className="ll-name">Project</div>
                </div>
                <div className="dv-lane-cards">
                  <div className="dv-gcard t-project">
                    <div className="gc-top">
                      <div className="dv-pj-icon" style={projectIconStyle(selectedProject.name)}>{projectInitials(selectedProject.name)}</div>
                      <span className="gc-id">{selectedProject.id.slice(0, 8)}</span>
                    </div>
                    <div className="gc-title">{selectedProject.name}</div>
                    <div className="gc-foot">
                      <span className="gc-children-pill">{fmtCount(selectedProject.workItemCount)} items</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Type lanes */}
              {LANE_ORDER.map(lane => {
                const items = byLane[lane] ?? []
                if (items.length === 0) return null
                const VISIBLE = 14
                const show = items.slice(0, VISIBLE)
                const rest = items.length - show.length
                return (
                  <div key={lane} className="dv-lane">
                    <div className="dv-lane-label">
                      <div className="ll-ct">{items.length}</div>
                      <div className="ll-name">{TYPE_DISPLAY[lane]}s</div>
                    </div>
                    <div className="dv-lane-cards">
                      {show.map(it => (
                        <button
                          key={it.id}
                          className={`dv-gcard t-${lane}${selectedItemId === it.id ? ' on' : ''}`}
                          onClick={() => setSelectedItemId(it.id)}
                        >
                          <div className="gc-top">
                            <span className={`dv-wi-mark ${lane}`} style={{ width: 8, height: 8 }} />
                            <span className="gc-id">#{it.id}</span>
                            <span className={`dv-state ${stateKey(it.state)}`} style={{ marginLeft: 'auto', fontSize: 10, padding: '1px 6px' }}>{it.state}</span>
                          </div>
                          <div className="gc-title">{it.title}</div>
                          <div className="gc-foot">
                            {it.assignedTo
                              ? <><span className="dv-av" style={avatarStyle(it.assignedTo)}>{initials(it.assignedTo)}</span><span>{it.assignedTo}</span></>
                              : <><span className="dv-av unassigned">?</span><span style={{ color: 'var(--ns-text-subtle)' }}>Unassigned</span></>}
                          </div>
                        </button>
                      ))}
                      {rest > 0 && <div className="dv-gcard placeholder">+ {fmtCount(rest)} more {TYPE_DISPLAY[lane].toLowerCase()}{rest === 1 ? '' : 's'}</div>}
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      </div>

      {/* RIGHT — detail */}
      <div className="dv-pane dv-detail-pane">
        {!selectedItem && (
          <div className="dv-detail-empty">
            <CircleDot size={28} style={{ color: 'var(--ns-border-strong)', marginBottom: 12 }} />
            <div>Pick a work item from the canvas to see details.</div>
          </div>
        )}
        {selectedItem && <WorkItemDetail item={selectedItem} itemsById={itemsById} onPickChild={setSelectedItemId} />}
      </div>
    </div>
  )
}

function WorkItemDetail({
  item, itemsById, onPickChild,
}: {
  item:        AdoWorkItem
  itemsById:   Map<number, AdoWorkItem>
  onPickChild: (id: number) => void
}) {
  const tk = typeKey(item.workItemType)
  const sprint = item.iterationPath ? item.iterationPath.split(/[/\\]/).pop() : null
  const parent = item.parentId != null ? itemsById.get(item.parentId) ?? null : null
  const children = useMemo(() => {
    const out: AdoWorkItem[] = []
    for (const i of itemsById.values()) if (i.parentId === item.id) out.push(i)
    return out
  }, [item.id, itemsById])

  return (
    <>
      <div className="dv-detail-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span className={`dv-wi-mark ${tk}`} />
          <span className="dh-id">#{item.id}</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ns-text-subtle)' }}>{TYPE_DISPLAY[tk] ?? item.workItemType}</span>
        </div>
        <div className="dh-title">{item.title}</div>
        <div className="dh-meta">
          <span className={`dv-state ${stateKey(item.state)}`}>{item.state}</span>
          {sprint && <span className="dv-pill dv-pill-info">{sprint}</span>}
        </div>
      </div>

      <div className="dv-detail-section">
        <div className="ds-title">People</div>
        <div className="dv-kv-grid">
          <div className="dv-kv">
            <div className="k">Assigned to</div>
            <div className="v" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {item.assignedTo
                ? <><span className="dv-av" style={avatarStyle(item.assignedTo)}>{initials(item.assignedTo)}</span>{item.assignedTo}</>
                : <><span className="dv-av unassigned">?</span><span style={{ color: 'var(--ns-text-subtle)' }}>Unassigned</span></>}
            </div>
          </div>
          <div className="dv-kv">
            <div className="k">Project</div>
            <div className="v">{item.projectName}</div>
          </div>
        </div>
      </div>

      <div className="dv-detail-section">
        <div className="ds-title">Placement</div>
        <div className="dv-kv-grid">
          <div className="dv-kv"><div className="k">Iteration</div><div className="v mono">{item.iterationPath ?? '—'}</div></div>
          <div className="dv-kv"><div className="k">Area path</div><div className="v mono">{item.areaPath ?? '—'}</div></div>
        </div>
      </div>

      {parent && (
        <div className="dv-detail-section">
          <div className="ds-title">Parent</div>
          <button className="dv-child-item" onClick={() => onPickChild(parent.id)}>
            <span className={`dv-wi-mark ${typeKey(parent.workItemType)}`} />
            <span className="c-title">{parent.title}</span>
            <span className="dv-mini-tag">#{parent.id}</span>
          </button>
        </div>
      )}

      {children.length > 0 && (
        <div className="dv-detail-section">
          <div className="ds-title">Children · {children.length}</div>
          <div className="dv-child-list">
            {children.slice(0, 20).map(c => (
              <button key={c.id} className="dv-child-item" onClick={() => onPickChild(c.id)}>
                <span className={`dv-wi-mark ${typeKey(c.workItemType)}`} />
                <span className="c-title">{c.title}</span>
                <span className="dv-mini-tag">#{c.id}</span>
              </button>
            ))}
            {children.length > 20 && <div style={{ fontSize: 12, color: 'var(--ns-text-subtle)', textAlign: 'center', padding: 6 }}>+ {children.length - 20} more</div>}
          </div>
        </div>
      )}
    </>
  )
}

// ─── List panel ─────────────────────────────────────────────────────────────

interface ListFilters {
  search:    string
  projectId: string | ''
  type:      string | ''
  state:     'open' | 'closed' | 'all'
  assignee:  string | ''  // exact name or 'unassigned'
}

const PAGE_SIZE = 50

function ListPanel() {
  const projectsQ = useQuery({
    queryKey: ['azure-devops', 'projects'],
    queryFn:  () => api.get<AdoProject[]>('/azure-devops/projects'),
  })

  const [filters, setFilters] = useState<ListFilters>({
    search: '', projectId: '', type: '', state: 'open', assignee: '',
  })
  const [page, setPage] = useState(0)
  // Reset to page 0 whenever filters change.
  useEffect(() => { setPage(0) }, [filters.search, filters.projectId, filters.type, filters.state, filters.assignee])

  const qs = new URLSearchParams()
  if (filters.search)    qs.set('search',    filters.search)
  if (filters.projectId) qs.set('projectId', filters.projectId)
  if (filters.type)      qs.set('type',      filters.type)
  if (filters.state !== 'all') qs.set('state', filters.state)
  if (filters.assignee)  qs.set('assignee',  filters.assignee)
  qs.set('top',    String(PAGE_SIZE))
  qs.set('offset', String(page * PAGE_SIZE))

  const itemsQ = useQuery({
    queryKey: ['azure-devops', 'work-items', 'list', qs.toString()],
    queryFn:  () => api.get<AdoWorkItemsResponse>(`/azure-devops/work-items?${qs.toString()}`),
  })

  const totalPages = itemsQ.data ? Math.max(1, Math.ceil(itemsQ.data.total / PAGE_SIZE)) : 1
  const currentRange = itemsQ.data
    ? `${itemsQ.data.offset + 1}–${Math.min(itemsQ.data.offset + itemsQ.data.items.length, itemsQ.data.total)}`
    : ''

  return (
    <section>
      {/* Filter bar */}
      <div className="dv-filterbar">
        <div style={{ flex: 1, minWidth: 240, maxWidth: 360, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-subtle)' }} />
          <input
            className="search-input"
            placeholder="Search title or ID…"
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            style={{ paddingLeft: 30, width: '100%' }}
          />
        </div>

        <FilterChip label="Project" active={!!filters.projectId}>
          <select value={filters.projectId} onChange={e => setFilters({ ...filters, projectId: e.target.value })}>
            <option value="">All</option>
            {projectsQ.data?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </FilterChip>

        <FilterChip label="Type" active={!!filters.type}>
          <select value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })}>
            <option value="">All</option>
            {LANE_ORDER.map(k => <option key={k} value={ADO_TYPE_FOR_KEY[k]}>{TYPE_DISPLAY[k]}</option>)}
          </select>
        </FilterChip>

        <FilterChip label="State" active={filters.state !== 'open'}>
          <select value={filters.state} onChange={e => setFilters({ ...filters, state: e.target.value as ListFilters['state'] })}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </FilterChip>

        <FilterChip label="Assignee" active={!!filters.assignee}>
          <select value={filters.assignee} onChange={e => setFilters({ ...filters, assignee: e.target.value })}>
            <option value="">Anyone</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </FilterChip>

        <button className="dv-chip" onClick={() => setFilters({ search: '', projectId: '', type: '', state: 'open', assignee: '' })} title="Reset filters">
          <Filter size={11} />Reset
        </button>

        <div className="dv-filter-summary">
          {itemsQ.data
            ? <><strong>{fmtCount(itemsQ.data.total)}</strong> match{itemsQ.data.total === 1 ? '' : 'es'}</>
            : 'Loading…'}
        </div>
      </div>

      {/* Table */}
      <div className="dv-card dv-list-card">
        <div style={{ maxHeight: 'calc(100vh - 340px)', minHeight: 380, overflow: 'auto' }}>
          <table className="dv-table">
            <thead className="dv-list-thead">
              <tr>
                <th style={{ width: 130 }}>Type / ID</th>
                <th>Title</th>
                <th style={{ width: 110 }}>State</th>
                <th style={{ width: 200 }}>Project</th>
                <th style={{ width: 180 }}>Assignee</th>
                <th style={{ width: 170 }}>Iteration</th>
              </tr>
            </thead>
            <tbody>
              {itemsQ.isLoading && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--ns-text-subtle)' }}>Loading…</td></tr>}
              {itemsQ.isError && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--ns-err)' }}>{(itemsQ.error as Error).message}</td></tr>}
              {itemsQ.data?.items.map(it => {
                const tk = typeKey(it.workItemType)
                return (
                  <tr key={it.id} className="dv-list-row">
                    <td>
                      <div className="dv-wi">
                        <span className={`dv-wi-mark ${tk}`} />
                        <span className="dv-wi-id">#{it.id}</span>
                      </div>
                    </td>
                    <td className="title-cell"><div className="t1">{it.title}</div></td>
                    <td><span className={`dv-state ${stateKey(it.state)}`}>{it.state}</span></td>
                    <td>{it.projectName}</td>
                    <td>
                      {it.assignedTo
                        ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className="dv-av" style={avatarStyle(it.assignedTo)}>{initials(it.assignedTo)}</span>
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.assignedTo}</span>
                          </div>
                        : <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className="dv-av unassigned">?</span>
                            <span style={{ color: 'var(--ns-text-subtle)' }}>Unassigned</span>
                          </div>}
                    </td>
                    <td style={{ fontFamily: 'var(--dv-mono)', fontSize: 11.5, color: 'var(--ns-text-subtle)' }}>
                      {it.iterationPath ? it.iterationPath.split(/[/\\]/).pop() : '—'}
                    </td>
                  </tr>
                )
              })}
              {itemsQ.data && itemsQ.data.items.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', padding: 32 }}>No work items match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        <div className="dv-pager">
          <div>
            {itemsQ.data && <>Showing <strong style={{ color: 'var(--ns-text)' }}>{currentRange}</strong> of <strong style={{ color: 'var(--ns-text)' }}>{fmtCount(itemsQ.data.total)}</strong></>}
          </div>
          <div className="ctrls">
            <button disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Prev</button>
            <span style={{ padding: '4px 8px', color: 'var(--ns-text-muted)' }}>{page + 1} / {totalPages}</span>
            <button disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        </div>
      </div>
    </section>
  )
}

// ADO uses canonical work-item-type names — when the user picks "User Story"
// from the Type filter we want to query for "User Story", not "story".
const ADO_TYPE_FOR_KEY: Record<string, string> = {
  epic:    'Epic',
  feature: 'Feature',
  story:   'User Story',
  task:    'Task',
  bug:     'Bug',
  issue:   'Issue',
  test:    'Test Case',
}

function FilterChip({ label, active, children }: { label: string; active: boolean; children: React.ReactNode }) {
  return (
    <label className={`dv-chip${active ? ' active' : ''}`}>
      <span>{label}:</span>
      {children}
    </label>
  )
}
